import {
  BodyTextVariant,
  TextVariant,
  TextWeight,
  Theme,
} from "cms/theme/theme";
import { css } from "styled-components";

export const getTextColor = (theme: Theme, color?: string) => {
  if (!color) return theme.colors.tokens.text.textDefault;
  if (color === "inherit") return "inherit";
  if (color === "default") return theme.colors.tokens.text.textDefault;
  if (color === "disabled") return theme.colors.tokens.text.textDisabled;
  if (color === "activated") return theme.colors.tokens.text.textActivated;

  return color;
};

export const getMagicTextColor = (theme: Theme) => {
  return css`
    background: linear-gradient(0deg, #6419FF, #A881FE);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent !important;
  `;
};

export const getFontWeight = (
  theme: Theme,
  variant: TextVariant,
  weight?: TextWeight,
) => {
  let patchedWeight = weight;

  if (!patchedWeight) {
    if (
      variant.startsWith("H") ||
      variant.startsWith("D") ||
      variant.startsWith("L")
    ) {
      patchedWeight = "medium";
    } else {
      patchedWeight = "regular";
    }
  }

  if (!theme.text.variants[variant]) {
    console.error(`getFontWeight: Variant ${variant} not found in theme`);
    return "regular";
  }

  // @ts-expect-error
  if (!theme.text.variants[variant].weights[patchedWeight]) {
    console.error(`getFontWeight: Weight ${patchedWeight} not found in theme`);
    return "regular";
  }

  // @ts-expect-error
  return theme.text.variants[variant].weights[patchedWeight];
};

export const getTextEllipsis = () => {
  return css`
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;
};

export const getTextStyle = (
  theme: Theme,
  variant: TextVariant,
  weight?: TextWeight | "inherit",
) => {
  return css`
    font-family: ${theme.text.variants[variant].fontFamily};
    font-size: ${theme.text.variants[variant].viewport.mobile.fontSize};
    line-height: ${theme.text.variants[variant].viewport.mobile.lineHeight};
    letter-spacing: ${
      theme.text.variants[variant].viewport.mobile.letterSpacing
    };

    ${(props) =>
      weight !== "inherit" &&
      `
      font-weight: ${getFontWeight(theme, variant, weight)};
    `}

    ${theme.breakpoints.up("medium")} {
      font-size: ${theme.text.variants[variant].viewport.tablet.fontSize};
      line-height: ${theme.text.variants[variant].viewport.tablet.lineHeight};
      letter-spacing: ${
        theme.text.variants[variant].viewport.tablet.letterSpacing
      };
    }

    ${theme.breakpoints.up("large")} {
      font-size: ${theme.text.variants[variant].viewport.desktop.fontSize};
      line-height: ${theme.text.variants[variant].viewport.desktop.lineHeight};
      letter-spacing: ${
        theme.text.variants[variant].viewport.desktop.letterSpacing
      };
    }
  `;
};

export const getRichTextStyle = (theme: Theme, variant: BodyTextVariant) => {
  return css`
    ${getTextStyle(theme, variant)}
    color: ${theme.colors.tokens.text.textDefault};

    p {
      ${getTextStyle(theme, variant)}
      color: ${theme.colors.tokens.text.textDefault};
    }

    h1 {
      ${getTextStyle(theme, "H1")}
      color: ${theme.colors.tokens.text.textActivated};
    }

    h2 {
      ${getTextStyle(theme, "H2")}
      color: ${theme.colors.tokens.text.textActivated};
    }

    h3 {
      ${getTextStyle(theme, "H3")}
      color: ${theme.colors.tokens.text.textActivated};
    }

    h4 {
      ${getTextStyle(theme, "H4")}
      color: ${theme.colors.tokens.text.textActivated};
    }

    h5 {
      ${getTextStyle(theme, "H5")}
      color: ${theme.colors.tokens.text.textActivated};
    }

    h6 {
      ${getTextStyle(theme, "H5")}
      color: ${theme.colors.tokens.text.textActivated};
    }

    * {
      margin: 1rem 0;
    }

    li {
      margin: 0.5rem 0;
    }

    *:first-child {
      margin-top: 0;
    }

    *:last-child {
      margin-bottom: 0;
    }
  `;
};
